import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { Card, Button, Row, Col, Form, FormControl, Modal, Spinner } from 'react-bootstrap';
import { FaDownload, FaSearch, FaEraser, FaCalendarDay, FaFileInvoice } from 'react-icons/fa';
import api from '../../api/apiweb-resumendiarioboletas.js';
import { TableSimple } from 'react-pagination-table';
import { tiposDisponiblesEmpresa, labelTipoDocumento } from '../../constants/tiposDocumento';
import { formatoMiles } from '../../utils/format';
import selectStyles from '../../utils/selectStyles';
import './styles.scss';

// Los DTE emitidos desde la web (facturas y notas de crédito/débito) no pasan por
// un cajero, así que llegan sin local ni device: se identifican con esta etiqueta
// en vez de dejar las celdas vacías.
const ORIGEN_WEB = 'Web';

function Search(props) {
  const [docType, setDocType] = useState('');
  const [folio, setFolio] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [searching, setSearching] = useState(false);
  const [modalDescarga, setModalDescarga] = useState(false);
  const [mensajeFallo, setMensajeFallo] = useState(false);
  const [modalDescargaMensaje, setModalDescargaMensaje] = useState('');
  const [responseData, setResponseData] = useState(null);
  const [totales, setTotales] = useState(null);
  const __DEV__ = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';

  // Incluye los tipos habilitados para la empresa: boletas, facturas y las notas
  // de crédito/débito emitidas desde la web, que también suman al resumen diario.
  const tiposDocOpciones = tiposDisponiblesEmpresa(props.empresas, undefined, { incluirNotas: true });
  const tipoUnico = tiposDocOpciones.length === 1 ? tiposDocOpciones[0].value : null;
  const docOptions = tiposDocOpciones.length > 1
    ? [{ value: '', label: 'Todos' }, ...tiposDocOpciones]
    : tiposDocOpciones;

  useEffect(() => {
    if (tipoUnico) setDocType(tipoUnico);
  }, [tipoUnico]);

  const ayer = () => {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() - 1);
    return fecha.toISOString().slice(0, 10);
  };

  const busquedaayer = async () => {
    setStartDate(ayer());
    setEndDate(ayer());
    busqueda(ayer(), ayer());
  };

  const busqueda = async (startDate, endDate) => {
    if (!startDate || !endDate || startDate > endDate) {
      setModalDescarga(true);
      setMensajeFallo(true);
      setModalDescargaMensaje('Selecciona un rango de fechas válido');
      return;
    }
    setModalDescarga(true);
    setMensajeFallo(false);
    setModalDescargaMensaje('Procesando');
    setSearching(true);
    api.getData({ docType, folio, startDate, endDate }).then(
      (data) => {
        if (__DEV__) { console.log(data); }
        setModalDescarga(false);
        // El backend devuelve el total como una fila con tipodocumento === 0 (device_id "Total").
        const totalRow = data.data.records.find((r) => Number(r.tipodocumento) === 0);
        const filas = data.data.records.filter((r) => Number(r.tipodocumento) !== 0);
        const records = filas.map((record) => ({
          ...record,
          tipodocumento: labelTipoDocumento(record.tipodocumento),
          local: record.local || ORIGEN_WEB,
          name: record.name || ORIGEN_WEB,
          cantidad: formatoMiles(record.cantidad),
          valorneto: formatoMiles(record.valorneto),
          valoriva: formatoMiles(record.valoriva),
          valorexento: formatoMiles(record.valorexento),
          valortotal: formatoMiles(record.valortotal),
          valorefectivo: formatoMiles(record.valorefectivo),
          valortarjeta: formatoMiles(record.valortarjeta),
        }));
        setResponseData(records);
        setTotales(totalRow || null);
        setSearching(false);
      },
    ).catch(() => {
      setSearching(false);
      setModalDescargaMensaje('Error en la descarga');
      setMensajeFallo(true);
    });
  };

  const descarga = async () => {
    if (!startDate || !endDate || startDate > endDate) {
      setModalDescarga(true);
      setMensajeFallo(true);
      setModalDescargaMensaje('Selecciona un rango de fechas válido');
      return;
    }
    setModalDescarga(true);
    setMensajeFallo(false);
    setModalDescargaMensaje('Generando archivo');
    try {
      await api.download({ docType, folio, startDate, endDate });
      setModalDescarga(false);
    } catch {
      setModalDescargaMensaje('Error en la descarga');
      setMensajeFallo(true);
    }
  };

  const limpiar = () => {
    setStartDate('');
    setEndDate('');
    setDocType(tipoUnico || '');
    setFolio('');
    setResponseData(null);
    setTotales(null);
  };

  const closeModal = () => {
    setModalDescarga(false);
  };

  return (
    <>
      <div className="Search resumen-diario">
        <div className="page-header">
          <h1 className="page-title">Resumen Diario de Documentos</h1>
          <p className="page-subtitle">Totales diarios de documentos emitidos por local y cajero</p>
        </div>

        <Card className="search-card">
          <Card.Header className="search-card-header">
            <FaSearch className="me-2" />
            Filtros
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col sm={12} md={12} lg={4}>
                <Form.Label htmlFor="docType">Tipo Documento</Form.Label>
                <Select
                  inputId="docType"
                  options={docOptions}
                  value={docOptions.find((o) => o.value === docType) || null}
                  onChange={(opt) => setDocType(opt ? opt.value : '')}
                  styles={selectStyles}
                  isSearchable={false}
                  placeholder="Seleccione"
                />
              </Col>
              <Col sm={12} md={6} lg={4}>
                <Form.Label htmlFor="startDate">Desde</Form.Label>
                <FormControl id="startDate" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Col>
              <Col sm={12} md={6} lg={4}>
                <Form.Label htmlFor="endDate">Hasta</Form.Label>
                <FormControl id="endDate" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Col>
            </Row>

            <div className="search-actions">
              <Button variant="primary" disabled={searching} onClick={() => busqueda(startDate, endDate)}>
                <FaSearch className="me-2" />Buscar
              </Button>
              <Button variant="outline-primary" disabled={searching} onClick={busquedaayer}>
                <FaCalendarDay className="me-2" />Ayer
              </Button>
              <Button variant="success" onClick={descarga}>
                <FaDownload className="me-2" />Descargar CSV
              </Button>
              <Button variant="outline-secondary" onClick={limpiar} className="ms-auto">
                <FaEraser className="me-2" />Limpiar
              </Button>
            </div>
          </Card.Body>
        </Card>

        <Card className="search-card mt-4">
          <Card.Header className="search-card-header">
            <FaFileInvoice className="me-2" />
            Resultados
            {responseData && responseData.length > 0 && (
              <span className="results-count">{responseData.length}</span>
            )}
          </Card.Header>
          <Card.Body>
            {responseData && responseData.length > 0 ? (
              <>
              {totales && (
                <div className="totales">
                  <div className="total-destacado">
                    <span className="total-destacado-label">Valor Total</span>
                    <span className="total-destacado-value">${formatoMiles(totales.valortotal)}</span>
                    <span className="total-destacado-sub">{formatoMiles(totales.cantidad)} documentos</span>
                  </div>
                  <div className="totales-grid">
                    <div className="total-item">
                      <span className="total-label">Valor Neto</span>
                      <span className="total-value">${formatoMiles(totales.valorneto)}</span>
                    </div>
                    <div className="total-item">
                      <span className="total-label">Valor IVA</span>
                      <span className="total-value">${formatoMiles(totales.valoriva)}</span>
                    </div>
                    <div className="total-item">
                      <span className="total-label">Valor Exento</span>
                      <span className="total-value">${formatoMiles(totales.valorexento)}</span>
                    </div>
                    <div className="total-item">
                      <span className="total-label">Valor Efectivo</span>
                      <span className="total-value">${formatoMiles(totales.valorefectivo)}</span>
                    </div>
                    <div className="total-item">
                      <span className="total-label">Valor Tarjeta</span>
                      <span className="total-value">${formatoMiles(totales.valortarjeta)}</span>
                    </div>
                  </div>
                </div>
              )}
              <TableSimple
                className="resumendiarioboletas"
                headers={['Tipo Documento', 'Fecha Emisión', 'Local', 'Cajero', 'Cantidad', 'Valor Neto', 'Valor IVA', 'Valor Exento', 'Valor Total', 'Valor Efectivo', 'Valor Tarjeta']}
                data={responseData}
                columns="tipodocumento.fechaemision.local.name.cantidad.valorneto.valoriva.valorexento.valortotal.valorefectivo.valortarjeta"
                perPageItemCount={10}
                partialPageCount={3}
                totalCount={responseData.length}
                arrayOption={[['size', 'all', ' ']]}
                nextPageText="Siguiente"
                prePageText="Anterior"
              />
              </>
            ) : (
              <div className="empty-state">
                <FaFileInvoice className="empty-state-icon" />
                <p className="empty-state-text">
                  {responseData === null
                    ? 'Selecciona un rango de fechas y presiona "Buscar"'
                    : 'No se encontraron documentos en el rango seleccionado'}
                </p>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      <Modal show={modalDescarga} onHide={closeModal} centered>
        <Modal.Header closeButton={mensajeFallo}>
          <Modal.Title>Búsqueda de resumen diario</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          {!mensajeFallo && <Spinner animation="border" variant="primary" className="mb-3" />}
          <div className="modal-message">{modalDescargaMensaje}</div>
        </Modal.Body>
        {mensajeFallo && (
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cerrar</Button>
          </Modal.Footer>
        )}
      </Modal>
    </>
  );
}

export default Search;
